import { TenantInfo } from '../types';

/** Keys de sesión viva (por pestaña). */
const SESSION_KEYS = {
  TOKEN: 'token',
  REFRESH_TOKEN: 'refreshToken',
  COOKIE_SESSION: 'cookieSession',
  TAB_SESSION: 'tabSessionId',
  CSRF: 'csrfToken',
  TENANT_ID: 'tenantId',
  TENANT_NAME: 'tenantName',
  TENANT_CODE: 'tenantCode',
  TENANTS: 'tenants',
  ROLES: 'roles',
  USERNAME: 'username',
  DISPLAY_NAME: 'displayName',
  TWO_FACTOR_ENABLED: 'twoFactorEnabled',
} as const;

/**
 * Last-used en localStorage: solo preferencia de tenant para solapas nuevas (resume).
 * Roles/username/tenants no deben vivir acá (contaminan otras pestañas).
 */
const LAST_USED_KEYS = {
  TOKEN: 'token',
  REFRESH_TOKEN: 'refreshToken',
  COOKIE_SESSION: 'cookieSession',
  CSRF: 'csrfToken',
  TENANT_ID: 'tenantId',
  TENANT_NAME: 'tenantName',
  TENANT_CODE: 'tenantCode',
  /** Legacy: se limpia, ya no se escribe. */
  ROLES: 'roles',
  USERNAME: 'username',
  USER_NAME: 'userName',
  USER: 'user',
  DISPLAY_NAME: 'displayName',
  TENANTS_LIST: 'tenants',
} as const;

export const TAB_TENANT_QUERY = 'tabTenant';
export const TAB_HANDOFF_QUERY = 'tabHandoff';
export const AUTH_BROADCAST_CHANNEL = 'eone-auth';
const TAB_EXPLICIT_LOGOUT_KEY = 'tabExplicitLogout';
const SLOT_OWNER_PROBE_MS = 250;

export type AuthBroadcastMessage =
  | {
      type: 'token-refresh';
      tenantId: number | null;
      token?: string;
      refreshToken?: string;
      csrfToken?: string;
      roles?: string[];
      username?: string;
      displayName?: string;
      tenantName?: string | null;
      tenantCode?: string | null;
    }
  | { type: 'logout-all' }
  | { type: 'slot-probe'; tabSessionId: string }
  | { type: 'slot-owned'; tabSessionId: string };

export interface TabSessionSnapshot {
  token: string | null;
  refreshToken: string | null;
  tenantId: number | null;
  tenantName: string | null;
  tenantCode: string | null;
  roles: string[];
  username: string | null;
  displayName: string | null;
  twoFactorEnabled: boolean | null;
}

const safeGet = (store: Storage, key: string): string | null => {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (store: Storage, key: string, value: string): void => {
  try {
    store.setItem(key, value);
  } catch {
    // ignore quota / private mode
  }
};

const safeRemove = (store: Storage, key: string): void => {
  try {
    store.removeItem(key);
  } catch {
    // ignore
  }
};

/** El JWT vive en la cookie HttpOnly. No hay access token legible por JavaScript. */
export const getAccessToken = (): string | null => null;

export const getRefreshToken = (): string | null => null;

export const getCsrfToken = (): string | null => safeGet(sessionStorage, SESSION_KEYS.CSRF);

export const getTabSessionId = (): string | null => safeGet(sessionStorage, SESSION_KEYS.TAB_SESSION);

export const hasBrowserSession = (): boolean =>
  safeGet(sessionStorage, SESSION_KEYS.COOKIE_SESSION) === '1' && !!getTabSessionId();

/** Esta solapa cerró sesión o venció. No se comparte con las demás. */
export const markTabExplicitLogout = (): void => {
  safeSet(sessionStorage, TAB_EXPLICIT_LOGOUT_KEY, '1');
};

export const clearTabExplicitLogout = (): void => {
  safeRemove(sessionStorage, TAB_EXPLICIT_LOGOUT_KEY);
};

export const isTabExplicitLogout = (): boolean =>
  safeGet(sessionStorage, TAB_EXPLICIT_LOGOUT_KEY) === '1';

export const getLastUsedTenantId = (): number | null => {
  const v = safeGet(localStorage, LAST_USED_KEYS.TENANT_ID);
  if (!v) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

export const getSessionTenantId = (): number | null => {
  const v = safeGet(sessionStorage, SESSION_KEYS.TENANT_ID);
  if (!v) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

export const readTabSession = (): TabSessionSnapshot => {
  let roles: string[] = [];
  try {
    const raw = safeGet(sessionStorage, SESSION_KEYS.ROLES);
    roles = raw ? JSON.parse(raw) : [];
  } catch {
    roles = [];
  }
  const tid = safeGet(sessionStorage, SESSION_KEYS.TENANT_ID);
  return {
    token: safeGet(sessionStorage, SESSION_KEYS.TOKEN),
    refreshToken: safeGet(sessionStorage, SESSION_KEYS.REFRESH_TOKEN),
    tenantId: tid ? parseInt(tid, 10) : null,
    tenantName: safeGet(sessionStorage, SESSION_KEYS.TENANT_NAME),
    tenantCode: safeGet(sessionStorage, SESSION_KEYS.TENANT_CODE),
    roles,
    username: safeGet(sessionStorage, SESSION_KEYS.USERNAME),
    displayName: safeGet(sessionStorage, SESSION_KEYS.DISPLAY_NAME),
    twoFactorEnabled:
      safeGet(sessionStorage, SESSION_KEYS.TWO_FACTOR_ENABLED) === 'true'
        ? true
        : safeGet(sessionStorage, SESSION_KEYS.TWO_FACTOR_ENABLED) === 'false'
          ? false
          : null,
  };
};

export const setTenantsList = (tenants: TenantInfo[]): void => {
  // Migración: la lista ya no se comparte entre solapas.
  safeRemove(localStorage, LAST_USED_KEYS.TENANTS_LIST);
  if (tenants.length) {
    safeSet(sessionStorage, SESSION_KEYS.TENANTS, JSON.stringify(tenants));
  } else {
    safeRemove(sessionStorage, SESSION_KEYS.TENANTS);
  }
};

export const getTenantsList = (): TenantInfo[] => {
  try {
    const raw = safeGet(sessionStorage, SESSION_KEYS.TENANTS);
    if (raw) {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch {
    // ignore
  }
  // Migración one-shot: listados viejos en localStorage pasan a esta solapa y se borran del store compartido.
  try {
    const legacyRaw = safeGet(localStorage, LAST_USED_KEYS.TENANTS_LIST);
    if (!legacyRaw) return [];
    const legacy = JSON.parse(legacyRaw);
    if (!Array.isArray(legacy) || !legacy.length) {
      safeRemove(localStorage, LAST_USED_KEYS.TENANTS_LIST);
      return [];
    }
    setTenantsList(legacy);
    return legacy;
  } catch {
    safeRemove(localStorage, LAST_USED_KEYS.TENANTS_LIST);
    return [];
  }
};

export interface PersistSessionInput {
  token?: string;
  refreshToken?: string;
  csrfToken?: string | null;
  tabSessionId?: string | null;
  tenantId?: number | null;
  tenantName?: string | null;
  tenantCode?: string | null;
  roles?: string[];
  username?: string | null;
  displayName?: string | null;
  twoFactorEnabled?: boolean | null;
  /** Actualiza last-used en localStorage (default true). */
  updateLastUsed?: boolean;
}

/** Escribe la sesión viva de esta pestaña y opcionalmente el last-used global. */
const dropLegacyJwt = (): void => {
  safeRemove(sessionStorage, SESSION_KEYS.TOKEN);
  safeRemove(sessionStorage, SESSION_KEYS.REFRESH_TOKEN);
  safeRemove(localStorage, LAST_USED_KEYS.TOKEN);
  safeRemove(localStorage, LAST_USED_KEYS.REFRESH_TOKEN);
};

export const persistTabSession = (data: PersistSessionInput): void => {
  dropLegacyJwt();
  safeSet(sessionStorage, SESSION_KEYS.COOKIE_SESSION, '1');
  if (data.tabSessionId) {
    safeSet(sessionStorage, SESSION_KEYS.TAB_SESSION, data.tabSessionId);
  }
  if (data.csrfToken) {
    safeSet(sessionStorage, SESSION_KEYS.CSRF, data.csrfToken);
  }
  if (data.roles) {
    safeSet(sessionStorage, SESSION_KEYS.ROLES, JSON.stringify(data.roles));
  }
  if (data.username) {
    safeSet(sessionStorage, SESSION_KEYS.USERNAME, data.username);
  }
  if (data.displayName) {
    safeSet(sessionStorage, SESSION_KEYS.DISPLAY_NAME, data.displayName);
  }
  if (data.twoFactorEnabled === true || data.twoFactorEnabled === false) {
    safeSet(sessionStorage, SESSION_KEYS.TWO_FACTOR_ENABLED, String(data.twoFactorEnabled));
  } else if (data.twoFactorEnabled === null) {
    safeRemove(sessionStorage, SESSION_KEYS.TWO_FACTOR_ENABLED);
  }
  if (data.tenantId != null) {
    safeSet(sessionStorage, SESSION_KEYS.TENANT_ID, String(data.tenantId));
    safeSet(sessionStorage, SESSION_KEYS.TENANT_NAME, data.tenantName ?? '');
    safeSet(sessionStorage, SESSION_KEYS.TENANT_CODE, data.tenantCode ?? '');
  } else if (data.tenantId === null) {
    safeRemove(sessionStorage, SESSION_KEYS.TENANT_ID);
    safeRemove(sessionStorage, SESSION_KEYS.TENANT_NAME);
    safeRemove(sessionStorage, SESSION_KEYS.TENANT_CODE);
  }

  if (data.updateLastUsed !== false) {
    writeLastUsedFromSession();
  }
};

/** Preferencia de tenant para solapas nuevas (resume). Identidad y tenants quedan en sessionStorage. */
export const writeLastUsedFromSession = (): void => {
  const snap = readTabSession();
  if (!hasBrowserSession()) return;
  dropLegacyJwt();
  safeRemove(localStorage, LAST_USED_KEYS.COOKIE_SESSION);
  safeRemove(localStorage, LAST_USED_KEYS.CSRF);
  safeRemove(localStorage, LAST_USED_KEYS.ROLES);
  safeRemove(localStorage, LAST_USED_KEYS.USERNAME);
  safeRemove(localStorage, LAST_USED_KEYS.USER_NAME);
  safeRemove(localStorage, LAST_USED_KEYS.USER);
  safeRemove(localStorage, LAST_USED_KEYS.DISPLAY_NAME);
  safeRemove(localStorage, LAST_USED_KEYS.TENANTS_LIST);
  if (snap.tenantId != null) {
    safeSet(localStorage, LAST_USED_KEYS.TENANT_ID, String(snap.tenantId));
    safeSet(localStorage, LAST_USED_KEYS.TENANT_NAME, snap.tenantName ?? '');
    safeSet(localStorage, LAST_USED_KEYS.TENANT_CODE, snap.tenantCode ?? '');
  }
};

/** La solapa nueva no copia la ranura. Entra por resume de la cookie o por un handoff. */
export const seedSessionFromLastUsedIfNeeded = (): TabSessionSnapshot => {
  dropLegacyJwt();
  safeRemove(localStorage, LAST_USED_KEYS.COOKIE_SESSION);
  safeRemove(localStorage, LAST_USED_KEYS.CSRF);
  return readTabSession();
};

/** Limpia sesión de esta pestaña. */
export const clearTabSession = (): void => {
  Object.values(SESSION_KEYS).forEach((key) => safeRemove(sessionStorage, key));
};

/** Limpia last-used global (tokens + preferencia de tenant + leftovers legacy). */
export const clearLastUsedAuth = (): void => {
  safeRemove(localStorage, LAST_USED_KEYS.TOKEN);
  safeRemove(localStorage, LAST_USED_KEYS.REFRESH_TOKEN);
  safeRemove(localStorage, LAST_USED_KEYS.COOKIE_SESSION);
  safeRemove(localStorage, LAST_USED_KEYS.CSRF);
  safeRemove(localStorage, LAST_USED_KEYS.TENANT_ID);
  safeRemove(localStorage, LAST_USED_KEYS.TENANT_NAME);
  safeRemove(localStorage, LAST_USED_KEYS.TENANT_CODE);
  safeRemove(localStorage, LAST_USED_KEYS.ROLES);
  safeRemove(localStorage, LAST_USED_KEYS.USERNAME);
  safeRemove(localStorage, LAST_USED_KEYS.USER_NAME);
  safeRemove(localStorage, LAST_USED_KEYS.USER);
  safeRemove(localStorage, LAST_USED_KEYS.DISPLAY_NAME);
  safeRemove(localStorage, LAST_USED_KEYS.TENANTS_LIST);
};

export const clearAllAuthStorage = (): void => {
  clearTabSession();
  clearLastUsedAuth();
};

let authChannel: BroadcastChannel | null = null;

export const getAuthBroadcastChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!authChannel) {
    try {
      authChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    } catch {
      authChannel = null;
    }
  }
  return authChannel;
};

export const broadcastAuthMessage = (message: AuthBroadcastMessage): void => {
  try {
    getAuthBroadcastChannel()?.postMessage(message);
  } catch {
    // ignore
  }
};

/**
 * Una pestaña duplicada copia el sessionStorage y compartiría la ranura.
 * La solapa ya abierta responde; una recarga no tiene quien conteste y conserva la suya.
 */
export const anotherTabOwnsSlot = (tabSessionId: string, signal?: AbortSignal): Promise<boolean> => {
  const channel = getAuthBroadcastChannel();
  if (!channel || signal?.aborted) return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (taken: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      channel.removeEventListener('message', onMessage);
      signal?.removeEventListener('abort', onAbort);
      resolve(taken);
    };
    const onMessage = (event: MessageEvent<AuthBroadcastMessage>) => {
      const msg = event.data;
      if (msg?.type === 'slot-owned' && msg.tabSessionId === tabSessionId) finish(true);
    };
    const onAbort = () => finish(false);
    const timer = window.setTimeout(() => finish(false), SLOT_OWNER_PROBE_MS);
    signal?.addEventListener('abort', onAbort, { once: true });
    channel.addEventListener('message', onMessage);
    broadcastAuthMessage({ type: 'slot-probe', tabSessionId });
  });
};

/** Lee y quita ?tabTenant= de la URL. */
export const consumeTabTenantQuery = (): number | null => {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(TAB_TENANT_QUERY);
    if (!raw) return null;
    const id = parseInt(raw, 10);
    url.searchParams.delete(TAB_TENANT_QUERY);
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
};

export const buildOpenTenantUrl = (tenantId: number, path?: string): string => {
  const base = path || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const url = new URL(base, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  url.searchParams.set(TAB_TENANT_QUERY, String(tenantId));
  return url.pathname + url.search + url.hash;
};

export const consumeTabHandoffQuery = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const code = url.searchParams.get(TAB_HANDOFF_QUERY);
    if (!code) return null;
    url.searchParams.delete(TAB_HANDOFF_QUERY);
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    return code;
  } catch {
    return null;
  }
};

export const buildHandoffUrl = (code: string, path?: string): string => {
  const current = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
  const url = new URL(path || current, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  url.searchParams.delete(TAB_TENANT_QUERY);
  url.searchParams.set(TAB_HANDOFF_QUERY, code);
  return url.pathname + url.search + url.hash;
};

const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

export const browserSessionHeaders = (method = 'get'): Record<string, string> => {
  const headers: Record<string, string> = {};
  const tab = getTabSessionId();
  if (tab) headers['X-Tab-Session'] = tab;
  if (UNSAFE_METHODS.has(method.toLowerCase())) {
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-TOKEN'] = csrf;
  }
  return headers;
};

export const applyBrowserSessionHeaders = (
  headers: { set?: (name: string, value: string) => void } & Record<string, unknown>,
  method?: string
): void => {
  const extra = browserSessionHeaders(method);
  Object.entries(extra).forEach(([name, value]) => {
    if (typeof headers.set === 'function') headers.set(name, value);
    else headers[name] = value;
  });
};
